import React, { useState, useRef, useEffect } from 'react'
import { ChevronDown } from 'lucide-react'
import { cn } from 'lib/utils'

export interface ModelOption {
  value: string
  label: string
  cost: number
  apiValue: string
  description?: string
}

const modelOptions: ModelOption[] = [
  { 
    value: 'nano-banana', 
    label: 'Nano Banana', 
    cost: 100,
    apiValue: 'gemini-2.5-flash-image',
    description: '기본 AI 이미지 생성 모델'
  },
  { 
    value: 'nano-banana-pro', 
    label: 'Nano Banana PRO', 
    cost: 400,
    apiValue: 'gemini-3-pro-image-preview',
    description: '고품질 AI 이미지 생성 모델'
  },
]

interface ModelSelectorProps {
  value: string
  onChange: (value: string, cost: number, apiValue: string) => void
  className?: string
}

export function ModelSelector({
  value,
  onChange,
  className,
}: ModelSelectorProps) {
  const [isOpen, setIsOpen] = useState(false)
  const selectorRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        selectorRef.current &&
        !selectorRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [])

  const handleSelect = (optionValue: string) => {
    const selectedOption = modelOptions.find(option => option.value === optionValue)
    if (selectedOption) {
      onChange(optionValue, selectedOption.cost, selectedOption.apiValue)
    }
    setIsOpen(false)
  }

  const selectedOption = modelOptions.find(
    (option) => option.value === value,
  )

  return (
    <div ref={selectorRef} className={cn('relative', className)}>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 rounded-xl border border-zinc-700/50 bg-zinc-800/50 px-3 py-2 text-xs font-medium text-main transition-colors hover:bg-zinc-700/50"
      >
        <span>{selectedOption?.label || 'Nano Banana'}</span>
        <ChevronDown
          size={12}
          className={cn(
            'transition-transform duration-200',
            isOpen && 'rotate-180',
          )}
        />
      </button>

      {isOpen && (
        <div className="absolute bottom-full left-0 mb-2 w-48 rounded-xl border border-zinc-700/50 bg-zinc-800/90 py-1 shadow-xl backdrop-blur-sm">
          {modelOptions.map((option) => (
            <button
              key={option.value}
              type="button"
              onClick={() => handleSelect(option.value)}
              className={cn(
                'block w-full px-3 py-2 text-left transition-colors hover:bg-zinc-700/50',
                option.value === value
                  ? 'bg-zinc-700/50 text-main'
                  : 'text-white',
              )}
            >
              <div className="flex items-center justify-between">
                <span className="text-xs font-medium">{option.label}</span>
                <div className="flex items-center gap-1">
                  <img
                    src="/assets/img/cash.png"
                    alt="cash"
                    className="h-[11px] w-[9px]"
                  />
                  <span className="text-xs">{option.cost}</span>
                </div>
              </div>
              {option.description && (
                <span className="text-xs text-zinc-400">{option.description}</span>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  )
}

export { modelOptions }